<template>
  <section class="mt-6 rounded-lg border border-slate-200 bg-gradient-to-r from-sky-900 to-sky-800 p-6 shadow-md">
    <header class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 class="text-2xl font-semibold text-white">Project Manager</h2>
        <p class="text-sm text-sky-200">Create and manage lab projects, assign inventory items and users.</p>
      </div>
      <form class="flex items-center gap-2" @submit.prevent="createProject">
        <input
          ref="projectName"
          placeholder="Project name"
          type="text"
          class="rounded-md border border-white/20 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400"
        />
        <button
          type="submit"
          class="inline-flex items-center rounded-md border border-sky-400/30 bg-white px-3 py-2 text-sm font-semibold text-sky-900 transition hover:bg-sky-50"
        >
          <i class="fas fa-plus mr-2"></i>
          Create Project
        </button>
      </form>
    </header>
  </section>

  <div v-if="appStore.state.projects.length > 0" class="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">
    <section
      v-for="(project, pindex) in appStore.state.projects"
      :key="canonicalId(project)"
      class="rounded-lg border border-slate-200 bg-white shadow overflow-hidden"
    >
      <div class="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-5 py-4">
        <div class="flex items-center gap-3 min-w-0">
          <div class="w-2 h-8 rounded-full bg-sky-600"></div>
          <h3 class="text-lg font-semibold text-sky-900 truncate">{{ project.name }}</h3>
        </div>
        <button
          type="button"
          @click="confirmDeleteId = canonicalId(project)"
          class="inline-flex items-center rounded-md border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50"
        >
          <i class="fas fa-trash-alt mr-1.5"></i>
          Delete
        </button>
      </div>

      <div class="flex flex-wrap gap-1 border-b border-slate-200 px-5">
        <button
          type="button"
          class="px-3 py-2 text-sm font-semibold rounded-t-lg transition"
          :class="tabClass(!projectTabState[pindex])"
          @click="projectTabState[pindex] = null"
        >
          Boxes
          <span class="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
            {{ project.boxes?.length || 0 }}
          </span>
        </button>
        <button
          type="button"
          class="px-3 py-2 text-sm font-semibold rounded-t-lg transition"
          :class="tabClass(projectTabState[pindex] == 'pucks')"
          @click="projectTabState[pindex] = 'pucks'"
        >
          Pucks
          <span class="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
            {{ project.pucks?.length || 0 }}
          </span>
        </button>
        <button
          type="button"
          class="px-3 py-2 text-sm font-semibold rounded-t-lg transition"
          :class="tabClass(projectTabState[pindex] == 'racks')"
          @click="projectTabState[pindex] = 'racks'"
        >
          Racks
          <span class="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
            {{ project.racks?.length || 0 }}
          </span>
        </button>
        <button
          type="button"
          class="px-3 py-2 text-sm font-semibold rounded-t-lg transition"
          :class="tabClass(projectTabState[pindex] == 'add')"
          @click="projectTabState[pindex] = 'add'"
        >
          Add User
        </button>
        <button
          type="button"
          class="px-3 py-2 text-sm font-semibold rounded-t-lg transition"
          :class="tabClass(projectTabState[pindex] == 'edit')"
          @click="
            () => {
              getProjectUsers(canonicalId(project));
              projectTabState[pindex] = 'edit';
            }
          "
        >
          Users
          <span class="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
            {{ users[canonicalId(project)]?.length || 0 }}
          </span>
        </button>
      </div>

      <div class="p-5 bg-slate-50/50">
        <div v-if="projectTabState[pindex] == 'add'">
          <custom-user-access
            @useradded="getProjectUsers(canonicalId(project))"
            container="project"
            :containerobj="{
              title: project.name,
              id: canonicalId(project),
              labId: project.labId,
            }"
            :index="pindex"
          ></custom-user-access>
        </div>

        <div v-else-if="projectTabState[pindex] == 'edit'">
          <p v-if="users[canonicalId(project)]?.length == 0" class="text-sm text-slate-500">
            No users added to project.
          </p>
          <div v-else class="space-y-2">
            <div
              v-for="user in users[canonicalId(project)]"
              :key="pindex + '_' + user.userId"
              class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <span class="text-sm text-slate-700">{{ user.email }}</span>
              <div class="flex items-center gap-3 text-sm">
                <label class="inline-flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    :checked="user.accesslevel != 'readonly'"
                    value="readwrite"
                    :id="user.userId + 'accesslevelrw' + canonicalId(project)"
                    :name="user.userId + 'accesslevels' + canonicalId(project)"
                    @click="editUserAccess($event, canonicalId(project), user.userId)"
                    class="cursor-pointer"
                  />
                  R+W
                </label>
                <label class="inline-flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    :checked="user.accesslevel == 'readonly'"
                    value="readonly"
                    :id="user.userId + 'accesslevelread' + canonicalId(project)"
                    :name="user.userId + 'accesslevels' + canonicalId(project)"
                    @click="editUserAccess($event, canonicalId(project), user.userId)"
                    class="cursor-pointer"
                  />
                  Read
                </label>
                <label class="inline-flex items-center gap-1 cursor-pointer text-red-600">
                  <input
                    type="radio"
                    value="delete"
                    :id="user.userId + 'accessleveldel' + canonicalId(project)"
                    :name="user.userId + 'accesslevels' + canonicalId(project)"
                    @click="editUserAccess($event, canonicalId(project), user.userId)"
                    class="cursor-pointer"
                  />
                  Remove
                </label>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="projectTabState[pindex] == 'racks'">
          <p v-if="!project.racks?.length" class="text-sm text-slate-500">No racks added to project.</p>
          <div v-else class="space-y-2">
            <div
              v-for="rack in project.racks"
              :key="canonicalId(rack) + '-' + project.name"
              class="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <div class="min-w-0">
                <span class="text-sm font-medium text-slate-700 truncate">{{ rack.customId || rack.serialNo }}</span>
                <span v-if="rack.title" class="ml-1 text-xs text-slate-500">({{ rack.title }})</span>
              </div>
              <button
                type="button"
                @click="removeBoxFromProject($event, canonicalId(rack), canonicalId(project), null, null, 'rack')"
                class="inline-flex items-center rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50"
              >
                <i class="fas fa-times mr-1"></i>
                Remove
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="projectTabState[pindex] == 'pucks'">
          <p v-if="!project.pucks?.length" class="text-sm text-slate-500">No pucks added to project.</p>
          <div v-else class="space-y-2">
            <div
              v-for="puck in project.pucks"
              :key="canonicalId(puck) + '-' + project.name"
              class="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <div class="min-w-0">
                <span class="text-sm font-medium text-slate-700 truncate">{{ puck.customId ?? puck.serialNo }}</span>
                <span v-if="puck.title" class="ml-1 text-xs text-slate-500">({{ puck.title }})</span>
              </div>
              <button
                type="button"
                @click="removeBoxFromProject($event, canonicalId(puck), canonicalId(project), null, null, 'puck')"
                class="inline-flex items-center rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50"
              >
                <i class="fas fa-times mr-1"></i>
                Remove
              </button>
            </div>
          </div>
        </div>

        <div v-else>
          <p v-if="!project.boxes?.length" class="text-sm text-slate-500">No boxes added to project.</p>
          <div v-else class="space-y-2">
            <div
              v-for="box in project.boxes"
              :key="canonicalId(box) + '-' + project.name"
              class="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <div class="min-w-0">
                <span class="text-sm font-medium text-slate-700 truncate">{{ box.customId }}</span>
                <span v-if="box.title" class="ml-1 text-xs text-slate-500">({{ box.title }})</span>
              </div>
              <button
                type="button"
                @click="removeBoxFromProject($event, canonicalId(box), canonicalId(project), box.wellNo, box.puckId)"
                class="inline-flex items-center rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50"
              >
                <i class="fas fa-times mr-1"></i>
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <section v-else class="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-12 text-center">
    <i class="fas fa-folder-open text-3xl text-slate-300 mb-3"></i>
    <p class="text-slate-500 text-lg font-semibold">No projects yet</p>
    <p class="text-slate-400 text-sm mt-1">Create a project above to get started.</p>
  </section>

  <div
    v-if="confirmDeleteId"
    class="fixed inset-0 z-50 flex items-center justify-center p-4"
    @keydown.escape="confirmDeleteId = null"
  >
    <div class="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" @click="confirmDeleteId = null"></div>
    <div
      class="relative bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-project-title"
    >
      <div class="px-6 pt-6 pb-2 text-center">
        <div class="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <span class="material-symbols-outlined text-red-600 text-2xl" aria-hidden="true">delete_forever</span>
        </div>
        <h3 id="delete-project-title" class="text-lg font-semibold text-slate-900">Delete Project</h3>
        <p class="mt-2 text-sm text-slate-600">
          Are you sure you want to delete
          <span class="font-semibold">{{ projectToDeleteName }}</span>?
          Boxes, pucks, and racks will be unlinked but not deleted.
        </p>
      </div>
      <div class="px-6 py-4 flex gap-3 justify-end">
        <button
          type="button"
          @click="confirmDeleteId = null"
          class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="doDeleteProject(confirmDeleteId)"
          class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CustomUserAccess from "./manage-products-sections/CustomUserAccess.vue";
import legacyInventoryTree from "../../../mixins/legacyInventoryTree";
import inventoryUiActions from "../../../mixins/inventoryUiActions";

export default {
  mixins: [legacyInventoryTree, inventoryUiActions],
  components: { CustomUserAccess },
  data() {
    return {
      users: {},
      labprojects: [],
      projectTabState: [],
      arrayOfProjectsForBoxes: [],
      confirmDeleteId: null,
    };
  },
  computed: {
    projectToDeleteName() {
      if (!this.confirmDeleteId) return "";
      const project = this.appStore.state.projects.find(
        (entry) => this.canonicalId(entry) === this.confirmDeleteId
      );
      return project?.name || "this project";
    },
  },
  async mounted() {
    await this.appStore.dispatch("getAdminStuff", {
      url: "projects",
    });
    for (const project of this.appStore.state.projects) {
      this.getProjectUsers(this.canonicalId(project));
    }
  },
  methods: {
    canonicalId(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value.id || "";
    },
    tabClass(active) {
      return active
        ? "bg-white border border-b-white border-slate-200 -mb-px text-sky-900"
        : "text-slate-500 hover:text-slate-700";
    },
    async getProjectUsers(projectid) {
      const data = await this.appStore.dispatch("getAdminStuff", {
        projusers: projectid,
      });
      this.users[projectid] = data;
    },
    async editUserAccess(ev, projectid, userid) {
      if (ev.currentTarget.value == "delete") {
        const ok = confirm("Are you sure you want to remove this user's access?");
        if (!ok) return;
      }
      const body = {
        userid,
        projectid,
      };

      if (ev.currentTarget.value == "delete") body.delete = true;
      else body.customaccess = ev.currentTarget.value;

      const data = await this.appStore.dispatch("updateItem", {
        objid: projectid,
        item: "projects",
        body,
        linkuser: true,
      });

      if (data == "deleted") {
        this.users[projectid] = this.users[projectid].filter(
          (obj) => obj.userId != userid
        );
      }
    },
    async getProjectBoxes() {
      this.arrayOfProjectsForBoxes = [];
      await this.ensureInventorySnapshotLoaded();
      const dewars = (this.legacyDewarData || []).flatMap(
        (labGroup) => labGroup?.data || []
      );
      for (let i = 0; i < dewars.length; i++) {
        const racks = dewars[i].racks;
        if (racks.length == 0) break;

        for (let j = 0; j < racks.length; j++) {
          const pucks = racks[j].pucks;
          if (pucks.length == 0) break;

          for (let k = 0; k < pucks.length; k++) {
            for (let i2 = 1; i2 <= 12; i2++) {
              if (pucks[k]["well" + i2]?.project) {
                const found = this.arrayOfProjectsForBoxes.some(
                  (el) => el.projid === pucks[k]["well" + i2].project
                );
                if (!found) {
                  this.arrayOfProjectsForBoxes.push({
                    projid: pucks[k]["well" + i2].project,
                    boxes: [],
                  });
                }

                this.arrayOfProjectsForBoxes.forEach((proj, index) => {
                  if (proj.projid == pucks[k]["well" + i2].project) {
                    const box = pucks[k]["well" + i2];
                    box.puckId = this.canonicalId(pucks[k]);
                    this.arrayOfProjectsForBoxes[index].boxes.push(box);
                  }
                });
              }
            }
          }
        }
      }
    },
    removeBoxFromProject(_ev, boxid, projectid, wellno, puckid, item = "box") {
      const ok = confirm(`Are you sure you want to remove this ${item}?`);
      if (!ok) return;
      const theobj = {
        item: "projects",
        objid: projectid,
        body: {
          pullvalue: {
            [item == "rack" ? "racks" : item == "puck" ? "pucks" : "boxes"]:
              boxid,
          },
        },
      };

      if (item == "box") {
        theobj.body.wellNo = wellno;
        theobj.body.puckId = this.canonicalId(puckid);
      }

      this.appStore.dispatch("updateItem", theobj).then(() => {
        this.appStore.dispatch("getAdminStuff", {
          url: "projects",
        });
      });
    },
    createProject() {
      if (!this.$refs.projectName.value) return;
      this.appStore
        .dispatch("createItem", {
          item: "projects",
          body: { name: this.$refs.projectName.value },
        })
        .then(() => {
          this.$refs.projectName.value = "";
          this.appStore.dispatch("getAdminStuff", {
            url: "projects",
          });
        });
    },
    doDeleteProject(id) {
      this.confirmDeleteId = null;
      this.deleteProject(id);
    },
    deleteProject(id) {
      this.appStore
        .dispatch("deleteItem", {
          item: "projects",
          objid: id,
        })
        .then(() => {
          this.appStore.dispatch("getAdminStuff", {
            url: "projects",
          });
        });
    },
  },
};
</script>
